import Layout from '../layout/layout'
import CustomersEntity from '@/entities/api/customers/customer'
import { useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { Link, useNavigate } from 'react-router-dom'
import {
	Dumbbell,
	Shirt,
	Watch,
	UserCheck,
	ChevronRight,
	Home,
	ArrowLeft,
	Compass,
	Star,
	TrendingUp,
	Heart,
	Users,
	CheckCircle2,
	ArrowUpRight,
	Sparkles,
	AlertTriangle,
	Search,
	MapPin,
} from 'lucide-react'
import { Entities } from '@/entities/api/entities'
import { entityToSpanish } from '@/utils/entity-convert'

const Lost = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const categories = useSelector(
		(state: RootState) => state.category.categories
	)
	const navigate = useNavigate()

	const getCategoryIcon = (type: Entities) => {
		switch (type) {
			case Entities.SUPPLEMENTS:
				return <Dumbbell className='w-6 h-6' />
			case Entities.CLOTHES:
				return <Shirt className='w-6 h-6' />
			case Entities.ACCESSORIES:
				return <Watch className='w-6 h-6' />
			case Entities.PLANS_PERSONALIZED:
				return <UserCheck className='w-6 h-6' />
			default:
				return <Compass className='w-6 h-6' />
		}
	}

	const getCategoryDetails = (type: Entities) => {
		switch (type) {
			case Entities.SUPPLEMENTS:
				return {
					title: 'Suplementos Premium',
					subtitle: 'Nutrición científicamente respaldada',
					description:
						'Fórmulas avanzadas desarrolladas con los más altos estándares de calidad para maximizar tu rendimiento',
					benefits: [
						'Absorción optimizada',
						'Ingredientes puros',
						'Sin aditivos artificiales',
					],
					gradient: 'from-red-50/80 via-white to-red-50/40',
					accent: 'red',
					stats: { customers: '1k+', satisfaction: '98%' },
				}
			case Entities.CLOTHES:
				return {
					title: 'Ropa Deportiva',
					subtitle: 'Tecnología en cada fibra',
					description:
						'Diseñada con tejidos técnicos de última generación que se adaptan a tu cuerpo y movimiento',
					benefits: [
						'Transpirable',
						'Flexibilidad total',
						'Durabilidad superior',
					],
					gradient: 'from-slate-50/80 via-white to-gray-50/40',
					accent: 'gray',
					stats: { customers: '1k+', satisfaction: '96%' },
				}
			case Entities.ACCESSORIES:
				return {
					title: 'Accesorios',
					subtitle: 'Completa tu experiencia',
					description:
						'Herramientas y accesorios esenciales que elevan tu entrenamiento al siguiente nivel',
					benefits: [
						'Diseño ergonómico',
						'Materiales premium',
						'Funcionalidad múltiple',
					],
					gradient: 'from-gray-50/80 via-white to-slate-50/40',
					accent: 'slate',
					stats: { customers: '1k+', satisfaction: '97%' },
				}
			case Entities.PLANS_PERSONALIZED:
				return {
					title: 'Planes Personalizados',
					subtitle: 'Tu ruta hacia el éxito',
					description:
						'Programas diseñados específicamente para tus objetivos, nivel y estilo de vida únicos',
					benefits: [
						'100% personalizado',
						'Seguimiento continuo',
						'Ajustes dinámicos',
					],
					gradient: 'from-red-50/60 via-white to-red-50/30',
					accent: 'red',
					stats: { customers: '1k+', satisfaction: '99%' },
				}
			default:
				return {
					title: 'Productos',
					subtitle: 'Calidad premium',
					description:
						'Selección curada de los mejores productos para tu desarrollo deportivo',
					benefits: ['Calidad garantizada', 'Envío rápido', 'Soporte experto'],
					gradient: 'from-gray-50/80 via-white to-slate-50/40',
					accent: 'gray',
					stats: { customers: '1k+', satisfaction: '97%' },
				}
		}
	}

	const getAccentClasses = (accent: string) => {
		const classes = {
			red: {
				border: 'border-l-red-500',
				bg: 'bg-red-500',
				text: 'text-red-600',
				hover: 'hover:bg-red-50/70',
				dot: 'bg-red-500',
				ring: 'ring-red-100',
				gradient: 'from-red-500 to-red-600',
				light: 'bg-red-50/50',
				icon: 'text-red-500',
			},
			gray: {
				border: 'border-l-gray-600',
				bg: 'bg-gray-600',
				text: 'text-gray-700',
				hover: 'hover:bg-gray-50/70',
				dot: 'bg-gray-500',
				ring: 'ring-gray-100',
				gradient: 'from-gray-600 to-gray-700',
				light: 'bg-gray-50/50',
				icon: 'text-gray-600',
			},
			slate: {
				border: 'border-l-slate-700',
				bg: 'bg-slate-700',
				text: 'text-slate-700',
				hover: 'hover:bg-slate-50/70',
				dot: 'bg-slate-600',
				ring: 'ring-slate-100',
				gradient: 'from-slate-700 to-slate-800',
				light: 'bg-slate-50/50',
				icon: 'text-slate-600',
			},
		}
		return classes[accent as keyof typeof classes] || classes.gray
	}

	const groupedCategories = categories.reduce((acc, category) => {
		if (!acc[category.type]) {
			acc[category.type] = []
		}
		acc[category.type].push(category)
		return acc
	}, {} as Record<Entities, typeof categories>)

	return (
		<Layout customer={customer} token={token}>
			<div className='min-h-screen bg-gradient-to-br from-slate-50 via-white to-gray-50'>
				<div className='fixed inset-0 overflow-hidden pointer-events-none'>
					<div className='absolute top-1/4 right-10 w-px h-32 bg-gradient-to-b from-transparent via-red-200/40 to-transparent opacity-60'></div>
					<div className='absolute top-1/2 left-16 w-2 h-2 bg-red-400/30 rounded-full animate-pulse'></div>
					<div className='absolute bottom-1/3 right-24 w-1 h-1 bg-gray-400/40 rounded-full'></div>
					<div className='absolute top-20 left-1/3 w-px h-16 bg-gradient-to-b from-transparent via-gray-200/30 to-transparent rotate-45'></div>
					<div className='absolute bottom-20 left-1/4 w-6 h-px bg-gradient-to-r from-transparent via-red-200/20 to-transparent'></div>
				</div>

				<div className='relative z-10 px-4 sm:px-6 lg:px-8 xl:px-12 pt-4 sm:pt-6 pb-2'>
					<div className='max-w-[95rem] mx-auto flex items-center justify-between'>
						<div className='flex items-center gap-2 sm:gap-3'>
							<button
								onClick={() => navigate(-1)}
								className='flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-white/90 backdrop-blur-md rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:border-gray-200 group'
							>
								<ArrowLeft className='w-4 h-4 text-gray-600 group-hover:text-gray-800 transition-colors' />
							</button>
							<Link
								to='/'
								className='flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 group'
							>
								<Home className='w-4 h-4 text-white group-hover:scale-105 transition-transform' />
							</Link>
						</div>
						<div className='flex items-center gap-2 text-sm text-gray-600'>
							<div className='w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse'></div>
							<span className='font-medium hidden sm:inline'>SportCenter Shop</span>
						</div>
					</div>
				</div>

				<div className='relative z-10 px-4 sm:px-6 lg:px-8 xl:px-12 py-8 sm:py-12'>
					<div className='max-w-[95rem] mx-auto'>
						<div className='text-center mb-8 sm:mb-12'>
							<div className='inline-flex items-center gap-2 bg-red-50/80 backdrop-blur-sm border border-red-100 px-4 py-2 rounded-full text-sm text-red-700 mb-6 shadow-sm'>
								<AlertTriangle className='w-4 h-4 text-red-500' />
								<span className='font-medium'>Página no encontrada</span>
							</div>

							<div className='relative'>
								<h1 className='text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light text-gray-900 mb-4 tracking-tight leading-tight'>
									¡Ups! Parece que
									<span className='block font-semibold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent mt-2'>
										te perdiste...
									</span>
								</h1>

								<div className='absolute -top-4 -right-4 sm:-top-6 sm:-right-6 w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center opacity-20'>
									<MapPin className='w-4 h-4 sm:w-6 sm:h-6 text-red-500' />
								</div>
							</div>

							<p className='text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed font-light mb-8'>
								La página que buscas no existe, pero no te preocupes. Aquí
								tienes las mejores opciones para encontrar exactamente lo que
								necesitas.
							</p>

							<div className='inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-gray-200 px-4 py-2 rounded-full text-sm text-gray-700 shadow-sm'>
								<Search className='w-4 h-4 text-red-500' />
								<span>Explora nuestras categorías principales</span>
							</div>
						</div>

						<div className='grid grid-cols-1 lg:grid-cols-2 gap-6 xl:gap-8 mb-8 sm:mb-12'>
							{Object.entries(groupedCategories).map(
								([entityType, entityCategories]) => {
									const type = entityType as Entities
									const details = getCategoryDetails(type)
									const accent = getAccentClasses(details.accent)

									return (
										<div key={type} className='group perspective-1000'>
											<div
												className={`relative bg-gradient-to-br ${details.gradient} rounded-3xl border border-gray-100/60 hover:border-gray-200/80 shadow-lg hover:shadow-2xl transition-all duration-700 overflow-hidden backdrop-blur-sm group-hover:scale-[1.02] group-hover:-translate-y-1`}
											>
												<div className='absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-red-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500'></div>

												<div className='absolute inset-0 opacity-[0.02]'>
													<div className='absolute top-4 right-4 w-24 h-24 bg-gradient-to-br from-red-500 to-red-600 rounded-full blur-3xl'></div>
													<div className='absolute bottom-6 left-6 w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full blur-2xl'></div>
												</div>

												<div className='relative p-6 lg:p-8'>
													<div className='relative mb-8'>
														<div className='flex items-start gap-5'>
															<div className='relative'>
																<div
																	className={`flex-shrink-0 p-4 bg-gradient-to-br ${accent.gradient} rounded-2xl shadow-xl relative overflow-hidden group-hover:scale-110 transition-transform duration-500`}
																>
																	<div className='absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent rounded-2xl'></div>
																	<div className='text-white relative z-10'>
																		{getCategoryIcon(type)}
																	</div>
																</div>
																<div
																	className={`absolute inset-0 bg-gradient-to-br ${accent.gradient} rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 -z-10`}
																></div>
															</div>

															<div className='flex-1 min-w-0'>
																<div className='flex items-center gap-2 mb-3'>
																	<div className='inline-flex items-center gap-1.5 bg-red-50/80 backdrop-blur-sm border border-red-100 px-3 py-1 rounded-full'>
																		<Star className='w-3 h-3 text-red-500 fill-current' />
																		<span className='text-xs font-semibold text-red-700'>
																			Premium
																		</span>
																	</div>
																	<div
																		className={`w-2 h-2 ${accent.dot} rounded-full animate-pulse`}
																	></div>
																</div>

																<h2 className='text-xl lg:text-2xl font-bold text-gray-900 tracking-tight mb-2 group-hover:text-gray-800 transition-colors'>
																	{details.title}
																</h2>
																<p
																	className={`text-sm font-semibold ${accent.text} mb-3`}
																>
																	{details.subtitle}
																</p>
																<p className='text-gray-600 text-sm leading-relaxed font-light'>
																	{details.description}
																</p>
															</div>
														</div>

														<Link
															to={`/Categoria/${entityToSpanish(type)}`}
															className='absolute top-0 right-0 flex items-center justify-center w-12 h-12 bg-white/90 backdrop-blur-md rounded-2xl border border-gray-100 hover:border-gray-200 transition-all duration-300 group/link shadow-lg hover:shadow-xl group-hover:scale-105'
														>
															<ArrowUpRight className='w-5 h-5 text-gray-600 group-hover/link:text-gray-800 group-hover/link:scale-110 transition-all' />
														</Link>
													</div>

													<div className='flex flex-wrap gap-2 mb-8'>
														{details.benefits.map((benefit, index) => (
															<div
																key={index}
																className={`group/benefit flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/70 backdrop-blur-sm border border-white/60 hover:border-gray-200 ${accent.hover} transition-all duration-300 shadow-sm hover:shadow-md hover:scale-105`}
															>
																<CheckCircle2
																	className={`w-4 h-4 ${accent.icon} flex-shrink-0 group-hover/benefit:scale-110 transition-transform`}
																/>
																<span className='text-sm text-gray-700 font-medium whitespace-nowrap'>
																	{benefit}
																</span>
															</div>
														))}
													</div>

													<div className='mb-8'>
														<h4 className='text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2'>
															<Compass className='w-4 h-4 text-red-500' />
															Explora subcategorías
														</h4>
														<div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
															{entityCategories
																.slice(0, 6)
																.map((category, index) => (
																	<Link
																		key={category._id}
																		to={`/SubCategoria/${entityToSpanish(
																			category.type
																		)}/${category.name.replace(/ /g, '-')}`}
																		className={`group/item relative p-4 rounded-xl bg-gradient-to-br from-white/80 to-white/60 backdrop-blur-sm border border-white/70 hover:border-gray-200 ${accent.hover} transition-all duration-500 text-center min-h-[80px] flex flex-col justify-center shadow-sm hover:shadow-lg hover:scale-105 hover:-translate-y-1`}
																		style={{
																			animationDelay: `${index * 100}ms`,
																		}}
																	>
																		<span className='text-sm text-gray-700 group-hover/item:text-gray-900 transition-colors font-semibold leading-tight'>
																			{category.name}
																		</span>
																	</Link>
																))}
														</div>
													</div>

													<div className='space-y-5'>
														<div className='flex items-center justify-between p-4 bg-gradient-to-r from-white/60 to-white/40 backdrop-blur-sm rounded-2xl border border-white/60'>
															<div className='flex items-center gap-6'>
																<div className='flex items-center gap-2'>
																	<div className='p-2 bg-gray-100/80 rounded-lg'>
																		<Users className='w-4 h-4 text-gray-600' />
																	</div>
																	<div>
																		<div className='text-lg font-bold text-gray-900'>
																			{details.stats.customers}
																		</div>
																		<div className='text-xs text-gray-600 font-medium'>
																			clientes
																		</div>
																	</div>
																</div>
																<div className='flex items-center gap-2'>
																	<div className='p-2 bg-red-100/80 rounded-lg'>
																		<Heart className='w-4 h-4 text-red-500' />
																	</div>
																	<div>
																		<div className='text-lg font-bold text-gray-900'>
																			{details.stats.satisfaction}
																		</div>
																		<div className='text-xs text-gray-600 font-medium'>
																			satisfacción
																		</div>
																	</div>
																</div>
															</div>
															<TrendingUp className='w-5 h-5 text-red-500' />
														</div>

														<Link
															to={`/Categoria/${entityToSpanish(type)}`}
															className={`flex items-center justify-center gap-3 w-full py-4 bg-gradient-to-r ${accent.gradient} text-white rounded-2xl text-sm font-semibold transition-all duration-300 group/link shadow-lg hover:shadow-xl hover:scale-[1.02] relative overflow-hidden`}
														>
															<div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover/link:translate-x-[100%] transition-transform duration-700'></div>

															<span className='relative z-10'>
																Explorar {details.title}
															</span>
															<ChevronRight className='w-4 h-4 group-hover/link:translate-x-1 transition-transform relative z-10' />
														</Link>
													</div>
												</div>
											</div>
										</div>
									)
								}
							)}
						</div>

						<div className='text-center'>
							<div className='max-w-sm mx-auto'>
								<div className='bg-gradient-to-br from-white/90 via-gray-50/80 to-white/90 backdrop-blur-sm rounded-2xl border border-gray-100 shadow-lg p-6 sm:p-8'>
									<div className='w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-md'>
										<Sparkles className='w-6 h-6 text-white' />
									</div>
									<h3 className='text-lg sm:text-xl font-semibold text-gray-900 mb-2'>
										¿Necesitas ayuda?
									</h3>
									<p className='text-gray-600 text-sm sm:text-base mb-6 font-light leading-relaxed'>
										Explora desde el inicio o déjanos guiarte hacia lo que
										realmente necesitas
									</p>
									<Link
										to='/'
										className='inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl text-sm font-medium hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-md hover:shadow-lg group'
									>
										<Home className='w-4 h-4 group-hover:scale-105 transition-transform' />
										<span>Volver al inicio</span>
									</Link>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	)
}

export default Lost
